import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CustomInput from "../../components/common/CustomInput";
import CustomTextArea from "../../components/common/CustomTextArea";
import SelectField from "../../components/common/CustomSelectProduct";
import CustomImageUploader from "../../components/common/CustomImageUploader";
import CustomTagSelector from "../../components/common/CustomTagSelector";
import CustomTabs from "../../components/common/CustomTabs";
import ColorSizeManager from "../../components/common/CustomColorSize";
import axios from "axios";
import config from "./../../config";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const UpdateProductModal = ({ open, onClose, onSave, productId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    baseprice: "",
    imageUrls: [],
    tags: [],
    sku: "",
    rating: 0,
    shortDescription: "",
    description: "",
    isNew: true,
    variations: [],
  });

  const [activeTab, setActiveTab] = useState(0);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!open) return;
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/Categories`);
        if (response.status === 200) setCategories(response.data);
      } catch (err) {
        console.error("❌ Lỗi khi lấy danh mục:", err);
      }
    };
    fetchCategories();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (open && productId && categories.length > 0) {
      axios
        .get(`${config.apiBaseUrl}/Products/${productId}`)
        .then((res) => {
          const product = res.data;
          const matchedCategory = categories.find(
            (c) => c.name === product.category
          );

          setFormData({
            name: product.name,
            category: matchedCategory
              ? matchedCategory.categoryId.toString()
              : "",
            baseprice: product.basePrice,
            imageUrls: product.imageUrls.map((img) =>
              typeof img === "string" ? { url: img } : { url: img.url }
            ),
            tags: product.tags,
            sku: product.sku,
            shortDescription: product.shortDescription || "",
            description: product.description || "",
            isNew: product.isNew,
            variations: product.variations,
          });
        })
        .catch((err) => console.error("❌ Lỗi khi lấy dữ liệu sản phẩm:", err));
    }
  }, [open, productId, categories]);

  const handleShortDescriptionChange = useCallback((val) => {
    setFormData((prev) => ({ ...prev, shortDescription: val }));
  }, []);

  const handleDescriptionChange = useCallback((val) => {
    setFormData((prev) => ({ ...prev, description: val }));
  }, []);

  const handleSave = async () => {
    try {
      const categoryId = parseInt(formData.category, 10);
      const basePrice = parseFloat(formData.baseprice);

      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("categoryId", categoryId.toString());
      formDataToSend.append("SKU", formData.sku);
      formDataToSend.append("BasePrice", basePrice.toString());
      formDataToSend.append("IsNew", formData.isNew ? "true" : "false");
      formDataToSend.append("ShortDescription", formData.shortDescription);
      formDataToSend.append("Description", formData.description);

      formData.variations.forEach((variation, i) => {
        formDataToSend.append(`Variations[${i}].Color`, variation.color);
        variation.sizes.forEach((size, j) => {
          formDataToSend.append(`Variations[${i}].Sizes[${j}].Size`, size.size);
          formDataToSend.append(
            `Variations[${i}].Sizes[${j}].Stock`,
            size.stock
          );
          formDataToSend.append(
            `Variations[${i}].Sizes[${j}].Price`,
            basePrice
          );
          formDataToSend.append(
            `Variations[${i}].Sizes[${j}].Status`,
            size.status
          );
        });
      });

      formData.tags.forEach((tag, index) => {
        formDataToSend.append(`Tags[${index}]`, tag);
      });

      formData.imageUrls.forEach((img) => {
        if (img.file) {
          formDataToSend.append("imageFiles", img.file);
        } else {
          formDataToSend.append("ExistingImageUrls", img.url);
        }
      });

      const response = await axios.put(
        `${config.apiBaseUrl}/Products/update/${productId}`,
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 200) {
        alert("✅ Cập nhật sản phẩm thành công!");

        const updatedProduct = {
          id: productId,
          name: formData.name,
          sku: formData.sku,
          category:
            categories.find(
              (c) => c.categoryId.toString() === formData.category
            )?.name || "",
          price: `$${parseFloat(formData.baseprice).toLocaleString()}`,
          quantity: formData.variations.reduce(
            (total, v) =>
              total + v.sizes.reduce((sum, s) => sum + Number(s.stock || 0), 0),
            0
          ),
          sales: 0,
          stockStatus: formData.variations.some((v) =>
            v.sizes.some((s) => Number(s.stock) > 0)
          )
            ? "In Stock"
            : "Out of Stock",
          rating: formData.rating,
          img: formData.imageUrls.length
            ? formData.imageUrls[0].url || formData.imageUrls[0]
            : "https://via.placeholder.com/50",
        };

        onSave?.(updatedProduct);
        onClose();
      } else {
        alert("❌ Có lỗi xảy ra khi cập nhật sản phẩm.");
      }
    } catch (err) {
      console.error("❌ Lỗi khi gửi yêu cầu cập nhật:", err);
      if (err.response) {
        alert(
          `❌ API: ${err.response.status} - ${
            err.response.data?.Message || "Không xác định"
          }`
        );
      } else {
        alert("❌ Không thể kết nối tới máy chủ!");
      }
    }
  };

  const handleImageUpload = (images) => {
    setFormData((prev) => ({ ...prev, imageUrls: images }));
  };

  // Responsive tabs
  const tabs = isMobile 
    ? ["Chung", "Thêm", "Hình", "Màu"] 
    : ["Thông tin chung", "Thông tin thêm", "Hình ảnh", "Màu & Size"];
  
  const fullTabNames = {
    "Chung": "Thông tin chung",
    "Thêm": "Thông tin thêm", 
    "Hình": "Hình ảnh",
    "Màu": "Màu & Size"
  };

  const getFullTabName = () => {
    const currentTab = tabs[activeTab];
    return fullTabNames[currentTab] || currentTab;
  };

  const groupedFields = {
    "Thông tin chung": [
      {
        label: "Mã SKU",
        component: (
          <CustomInput
            label="Mã SKU"
            value={formData.sku}
            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
            fullWidth
          />
        ),
      },
      {
        label: "Tên hàng",
        component: (
          <CustomInput
            label="Tên hàng"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
          />
        ),
      },
      {
        label: "Phân loại",
        component: (
          <SelectField
            label="Phân loại"
            value={formData.category}
            onChange={(e) =>
              setFormData({ ...formData, category: e.target.value })
            }
            options={categories.map((cat) => ({
              value: cat.categoryId.toString(),
              label: cat.name,
            }))}
            fullWidth
          />
        ),
      },
      {
        label: "Giá nhập",
        component: (
          <CustomInput
            label="Giá nhập"
            type="number"
            value={formData.baseprice}
            onChange={(e) =>
              setFormData({ ...formData, baseprice: e.target.value })
            }
            fullWidth
          />
        ),
      },
      {
        label: "Sản phẩm mới",
        component: (
          <FormControlLabel
            control={
              <Switch
                checked={formData.isNew}
                onChange={(e) =>
                  setFormData({ ...formData, isNew: e.target.checked })
                }
              />
            }
            label={isMobile ? "Mới" : "Sản phẩm mới"}
          />
        ),
      },
    ],
    "Thông tin thêm": [
      {
        label: "Mô tả ngắn",
        component: (
          <Box sx={{ width: "100%" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Mô tả ngắn
            </Typography>
            <ReactQuill
              key={`short-${productId}`}
              theme="snow"
              value={formData.shortDescription}
              onChange={handleShortDescriptionChange}
              modules={{
                toolbar: [
                  ["bold", "italic", "underline"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link"],
                ],
              }}
              style={{ minHeight: isMobile ? 100 : 120 }}
            />
          </Box>
        ),
      },
      {
        label: "Mô tả chi tiết",
        component: (
          <Box sx={{ width: "100%" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Mô tả chi tiết
            </Typography>
            <ReactQuill
              key={`desc-${productId}`}
              theme="snow"
              value={formData.description}
              onChange={handleDescriptionChange}
              modules={{
                toolbar: [
                  ["bold", "italic", "underline"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link", "image"],
                ],
              }}
              style={{ minHeight: isMobile ? 150 : 200 }}
            />
          </Box>
        ),
      },
    ],
    "Hình ảnh": [
      {
        label: "Tags",
        component: (
          <CustomTagSelector
            value={formData.tags}
            onChange={(tags) => setFormData({ ...formData, tags })}
            fullWidth={isMobile}
          />
        ),
      },
      {
        label: "Hình ảnh sản phẩm",
        component: (
          <CustomImageUploader
            onUpload={handleImageUpload}
            value={formData.imageUrls}
            onChange={handleImageUpload}
            compact={isMobile}
          />
        ),
      },
    ],
    "Màu & Size": [
      {
        label: "Quản lý Màu & Size",
        component: (
          <ColorSizeManager
            variations={formData.variations}
            setVariations={(variations) =>
              setFormData({ ...formData, variations })
            }
            compact={isMobile}
          />
        ),
      },
    ],
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "95%" : "90%",
          maxWidth: { xs: "95%", sm: "90%", md: "900px", lg: "1000px" },
          height: isMobile ? "85vh" : "70vh",
          bgcolor: "background.paper",
          boxShadow: 10,
          p: isMobile ? 2 : 3,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}
        >
          Cập nhật Sản Phẩm
        </Typography>
        
        <CustomTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabLabels={tabs}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile}
        />
        
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
          {getFullTabName()}
        </Typography>
        
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: isMobile ? 1 : 2,
            height: "calc(100% - 150px)",
          }}
        >
          {activeTab === tabs.indexOf(isMobile ? "Hình" : "Hình ảnh") ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
              {groupedFields["Hình ảnh"].map((field, index) => (
                <Box key={index} sx={{ width: "100%", maxWidth: isMobile ? "100%" : "500px", mb: 2 }}>
                  {field.component}
                </Box>
              ))}
            </Box>
          ) : activeTab === tabs.indexOf(isMobile ? "Màu" : "Màu & Size") ? (
            <Box sx={{ width: "100%" }}>
              {groupedFields["Màu & Size"].map((field, index) => (
                <Box key={index} sx={{ width: "100%" }}>
                  {field.component}
                </Box>
              ))}
            </Box>
          ) : (
            <Grid container spacing={isMobile ? 1 : 2}>
              {groupedFields[getFullTabName()]?.map((field, index) => (
                <Grid
                  item
                  xs={12}
                  sm={getFullTabName() === "Thông tin chung" ? 6 : 12}
                  md={getFullTabName() === "Thông tin chung" ? 4 : 12}
                  key={index}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  {field.component}
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
        
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 2,
            p: 1,
            borderTop: "1px solid #ddd",
            gap: isMobile ? 1 : 0,
            flexDirection: isMobile ? "column" : "row"
          }}
        >
          <Button
            variant="contained"
            color="success"
            sx={{ 
              width: isMobile ? "100%" : "48%", 
              fontWeight: "bold",
              fontSize: isMobile ? "0.875rem" : "1rem"
            }}
            onClick={handleSave}
          >
            Cập nhật
          </Button>
          <Button
            variant="outlined"
            color="error"
            sx={{ 
              width: isMobile ? "100%" : "48%", 
              fontWeight: "bold",
              fontSize: isMobile ? "0.875rem" : "1rem"
            }}
            onClick={onClose}
          >
            Hủy
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpdateProductModal;